import {
  DetailedRFQForm,
} from "@b2b/ui-components";
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { rfqService, adminService } from "@b2b/services";
import { XCircle, X } from "lucide-react";

interface ToastState {
  type: "success" | "error";
  message: string;
}

interface CategoryHierarchy {
  id: string;
  name: string;
  slug: string;
  parentId?: string;
  level?: number;
  children?: CategoryHierarchy[];
  productCount?: number;
}

export default function RfqEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [toast, setToast] = useState<ToastState | null>(null);
  const [categories, setCategories] = useState<CategoryHierarchy[]>([]);
  const [initialData, setInitialData] = useState<Record<string, any> | null>(null);
  const [rfqStatus, setRfqStatus] = useState<string>("");

  // Fetch categories with hierarchy on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await adminService.getCategories(true); // Fetch with hierarchy
        setCategories(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    fetchCategories();
  }, []);

  // Fetch RFQ data
  useEffect(() => {
    if (!id) return;

    const fetchRfq = async () => {
      try {
        setFetching(true);
        const response = await rfqService.getRfqDetail(id);
        if (response.data) {
          const rfq = response.data;
          setRfqStatus(rfq.status);

          // Check if RFQ is DRAFT status
          if (rfq.status !== "DRAFT") {
            showToast("error", "Only DRAFT RFQs can be edited");
            navigate(`/buyer/rfq/${id}`);
            return;
          }

          // Map RFQ data to form format
          const formData: Record<string, any> = {
            title: rfq.title,
            description: rfq.description,
            quantity: rfq.quantity,
            unit: rfq.unit,
            categoryIds: rfq.categories?.map((c: any) => c.id) || [],
            tags: rfq.tags || [],
            preferredDeliveryDate: rfq.preferredDeliveryDate 
              ? new Date(rfq.preferredDeliveryDate).toISOString().split('T')[0]
              : undefined,
            expiryDate: rfq.expiryDate 
              ? new Date(rfq.expiryDate).toISOString().split('T')[0]
              : undefined,
          };

          setInitialData(formData);
        }
      } catch (error: any) {
        console.error("Error fetching RFQ:", error);
        showToast("error", error?.response?.data?.message || "Failed to load RFQ");
        navigate("/buyer/rfq_list");
      } finally {
        setFetching(false);
      }
    };

    fetchRfq();
  }, [id, navigate]);

  const showToast = (type: "success" | "error", message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 5000);
  };

  const handleSubmit = async (formData: Record<string, any>) => {
    if (!id) return;

    // Validate status is still DRAFT
    if (rfqStatus !== "DRAFT") {
      showToast("error", "Only DRAFT RFQs can be edited");
      return;
    }

    try {
      setLoading(true);
      
      // Map form data to API format
      const payload = {
        title: formData.title || formData.leadName || "RFQ Request",
        description: formData.description || "",
        quantity: parseFloat(formData.quantity) || 1,
        unit: formData.unit || "pieces",
        categoryIds: formData.categoryIds || [],
        tags: formData.tags || [],
        preferredDeliveryDate: formData.preferredDeliveryDate,
        expiryDate: formData.expiryDate,
        status: formData.saveAsDraft ? "DRAFT" : "OPEN",
      };

      const response = await rfqService.updateRfq(id, payload);
      
      if (response.data?.id) {
        showToast("success", "RFQ updated successfully!");
        navigate(`/buyer/rfq/${id}`);
      } else {
        showToast("error", "Failed to update RFQ");
      }
    } catch (error: any) {
      console.error("Error updating RFQ:", error);
      const errorMessage = error?.response?.data?.message || "Failed to update RFQ";
      showToast("error", errorMessage);
      
      // If error is about non-DRAFT status, redirect
      if (errorMessage.includes("DRAFT") || errorMessage.includes("status")) {
        setTimeout(() => navigate(`/buyer/rfq/${id}`), 2000);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDraft = async (formData: Record<string, any>) => {
    await handleSubmit({ ...formData, saveAsDraft: true });
  };

  if (fetching) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading RFQ...</p>
        </div>
      </div>
    );
  }

  if (!initialData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground">RFQ not found or cannot be edited</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Toast Notification */}
      {toast && (
        <div
          className={`fixed top-4 right-4 z-50 flex items-center gap-2 p-4 rounded-lg shadow-lg max-w-[400px] ${
            toast.type === "success"
              ? "bg-green-500 text-white"
              : "bg-red-500 text-white"
          }`}
        >
          <XCircle className="w-5 h-5 flex-shrink-0" />
          <p className="flex-1 font-medium text-sm">{toast.message}</p>
          <button
            onClick={() => setToast(null)}
            className="ml-2 hover:opacity-80 transition-opacity flex-shrink-0"
            aria-label="Close notification"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="p-4">
        <DetailedRFQForm 
          switchToQuick={() => navigate(`/buyer/rfq/${id}`)}
          onSubmit={handleSubmit}
          onSaveDraft={handleSaveDraft}
          loading={loading}
          categories={categories}
          initialData={initialData}
          isEditMode={true}
        />
      </div>
    </div>
  );
}

